import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowRight, Check } from "lucide-react";
import { BRAND, PRICING } from "@/lib/config";

const includes = [
  "21 curated daily WhatsApp messages",
  "One 10–15 minute activity every morning",
  "Science, stories & question prompts",
  "Zero screen time for your child",
  "Private parent-support group",
  "Lifetime access to the archive",
];

export default function Pricing({ onBuyNow }) {
  return (
    <section
      id="pricing"
      className="py-24 sm:py-32 bg-cream-alt"
      data-testid="pricing-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14 sm:mb-20"
        >
          <div className="lg:col-span-7">
            <div className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-terracotta mb-4">
              Launch pricing · A quiet celebration
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              One price. <br />
              One family. <span className="italic">A lifetime of wonder.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="font-sans text-base sm:text-lg text-ink/75 leading-relaxed">
              {`We're keeping the launch cohort small so we can be present for every parent. Once seats fill for this batch, pricing gently returns to ${PRICING.currency}${PRICING.mrp.toLocaleString("en-IN")}.`}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-ink/20 bg-cream"
          data-testid="pricing-card"
        >
          {/* Left: price */}
          <div className="lg:col-span-5 p-8 sm:p-12 border-b lg:border-b-0 lg:border-r border-ink/15">
            <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-3">
              The Wonder Time Cohort
            </div>
            <div className="font-serif text-2xl sm:text-3xl text-ink mb-8">
              21 days, one message a morning.
            </div>

            <div className="flex items-baseline gap-4 mb-2">
              <div className="font-serif text-6xl sm:text-7xl lg:text-8xl text-ink leading-none">
                ₹999
              </div>
              <div className="font-serif text-2xl sm:text-3xl text-ink/40 line-through">
                ₹2,999
              </div>
            </div>
            <div className="text-sm text-ink/60 mb-8">
              One-time payment · No renewals · GST included
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onBuyNow}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-terracotta text-cream px-6 py-4 text-base font-semibold hover:bg-terracotta-hover transition-colors btn-focus"
                data-testid="pricing-buy-now-btn"
              >
                Buy now — begin Monday
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={BRAND.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/25 px-6 py-4 text-base font-semibold text-ink hover:bg-ink hover:text-cream transition-colors btn-focus"
                data-testid="pricing-whatsapp-btn"
              >
                <FaWhatsapp className="text-whatsapp text-lg" />
                Ask us on WhatsApp
              </a>
            </div>
          </div>

          {/* Right: includes */}
          <div className="lg:col-span-7 p-8 sm:p-12">
            <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-6">
              {`What's inside`}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {includes.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-base sm:text-lg text-ink/85 font-sans"
                >
                  <Check className="w-5 h-5 mt-1 shrink-0 text-terracotta" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
